import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Tray, Trash, DownloadSimple, Envelope, Buildings, CircleNotch, User } from "@phosphor-icons/react";

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const allowed = can(user, "manage_integrations");

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  useEffect(() => { if (allowed) load(); }, [allowed]);

  if (!allowed) return <Navigate to="/app" replace />;

  const remove = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setConfirmId(null);
      toast.success("Lead borttagen");
    } catch { toast.error("Kunde inte ta bort"); }
  };

  const exportCsv = () => {
    if (!leads?.length) return;
    const rows = [["Namn", "E-post", "Företag", "Datum"]];
    leads.forEach((l) => rows.push([l.name || "", l.email || "", l.company || "", new Date(l.created_at).toLocaleString("sv-SE")]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `senzum-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-extrabold text-2xl flex items-center gap-2">
            <Tray size={26} weight="fill" className="text-[#0033FF]" /> Leads — broschyrförfrågningar
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Alla som begärt säljbroschyren från landningssidan. Följ upp som säljmöjligheter.</p>
        </div>
        <button data-testid="leads-export-csv" onClick={exportCsv} disabled={!leads?.length}
          className="inline-flex items-center gap-2 bg-white border border-zinc-200 hover:border-zinc-300 text-zinc-700 rounded-md px-4 py-2.5 font-semibold transition-colors disabled:opacity-50">
          <DownloadSimple size={17} weight="bold" /> Exportera CSV
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6">
        {leads === null ? (
          <div className="py-16 flex justify-center"><CircleNotch size={26} className="animate-spin text-[#0033FF]" /></div>
        ) : leads.length === 0 ? (
          <div data-testid="leads-empty" className="py-16 text-center">
            <div className="mx-auto h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center">
              <Tray size={24} weight="duotone" className="text-[#0033FF]" />
            </div>
            <h3 className="mt-4 font-display font-bold text-lg">Inga leads än</h3>
            <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">När någon begär broschyren via knappen på landningssidan dyker de upp här.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <span data-testid="leads-count" className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0033FF] bg-[#0033FF]/10 rounded-full px-3 py-1">
                {leads.length} {leads.length === 1 ? "lead" : "leads"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-zinc-400 border-b border-zinc-100">
                    <th className="py-2 pr-4 font-bold">Namn</th>
                    <th className="py-2 pr-4 font-bold">E-post</th>
                    <th className="py-2 pr-4 font-bold">Företag</th>
                    <th className="py-2 pr-4 font-bold">Datum</th>
                    <th className="py-2 font-bold text-right">Åtgärd</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-zinc-50 hover:bg-zinc-50/60">
                      <td className="py-3 pr-4 font-semibold flex items-center gap-1.5">
                        <User size={14} weight="bold" className="text-zinc-400" /> {l.name || "—"}
                      </td>
                      <td className="py-3 pr-4">
                        <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 text-[#0033FF] hover:underline">
                          <Envelope size={14} weight="bold" /> {l.email}
                        </a>
                      </td>
                      <td className="py-3 pr-4 text-zinc-600">
                        {l.company ? <span className="inline-flex items-center gap-1.5"><Buildings size={14} weight="bold" className="text-zinc-400" />{l.company}</span> : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="py-3 pr-4 text-zinc-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("sv-SE")}</td>
                      <td className="py-3 text-right">
                        {confirmId === l.id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <button data-testid={`lead-confirm-${l.id}`} onClick={() => remove(l.id)}
                              className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md px-2.5 py-1.5">Bekräfta</button>
                            <button onClick={() => setConfirmId(null)} className="text-xs text-zinc-500 px-1">Avbryt</button>
                          </span>
                        ) : (
                          <button data-testid={`lead-delete-${l.id}`} onClick={() => setConfirmId(l.id)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-red-600 transition-colors">
                            <Trash size={15} weight="bold" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
